import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import { useCallback } from 'react';

import { invalidateCampaignPeople } from '@/hooks/trpc/invalidateCampaignPeople';
import { api } from '@/utils/api';

/**
 * Refresh everything that renders a campaign's steps or state. Every mutation that changes a
 * campaign's state or one of its steps' states calls this, so the contact detail, the campaign's
 * enrolled list, its touch graph, its counts and the reminder views all move together. A hand-picked
 * query list per hook drifted: the step hooks kept invalidating queries no page mounts anymore and
 * skipped `cadencesForCards`, the enrolled list that replaced them, so resolving a step left that
 * list stale.
 *
 * Callers fire-and-forget it: these refetch in the background, and awaiting them would hold a
 * mutation's spinner (and whatever runs after `mutateAsync`) on the slowest of them.
 */
export const useInvalidateCadenceState = () => {
  const utils = api.useUtils();
  return useCallback(
    () =>
      Promise.all([
        // The whole router: the enrolled list (cadencesForCards), touch graph (contactsInFlow), and
        // the contact's campaigns section (forContact / idOrNull).
        utils.cadence.invalidate(),
        utils.contact.byId.invalidate(),
        utils.contact.interrupted.invalidate(),
        utils.cadenceTemplate.analytics.invalidate(),
        // The campaign page's People tab: its rows and counts.
        invalidateCampaignPeople(utils),
        // Finishing or skipping a step, or ending a campaign, deletes its reminders server-side. The
        // detail panel's reminder card and active-campaign pill both come from this bootstrap.
        utils.contactDetailBootstrap.invalidate(),
        utils.globalReminder.invalidate(),
      ]),
    [utils],
  );
};

export const useCadenceDeleteAllMutation = () => {
  const utils = api.useUtils();
  const invalidateCadenceState = useInvalidateCadenceState();
  return api.cadenceBulk.bulkDelete.useMutation({
    onSuccess: async () => {
      void invalidateCadenceState();
      await Promise.all([
        utils.contact.contentViewed.invalidate(),
        utils.contact.responded.invalidate(),
        utils.contact.search.invalidate(),
        utils.history.contact.invalidate(),
      ]);
      void utils.cadenceTemplate.idV2.invalidate();
    },
  });
};

export const useCadenceDeleteMutation = () => {
  const utils = api.useUtils();
  const invalidateCadenceState = useInvalidateCadenceState();
  return api.cadenceBulk.delete.useMutation({
    onSuccess: async () => {
      void invalidateCadenceState();
      await Promise.all([
        utils.contact.contentViewed.invalidate(),
        utils.contact.responded.invalidate(),
        utils.history.contact.invalidate(),
        utils.cadenceTemplate.candidateListForContact.invalidate(),
        utils.cadenceTemplate.candidateCounts.invalidate(),
      ]);
      void utils.cadenceTemplate.idV2.invalidate();
    },
  });
};

export const useCadenceDeleteFutureStepsMutation = () => {
  const utils = api.useUtils();
  const invalidateCadenceState = useInvalidateCadenceState();
  return api.cadence.deleteFutureSteps.useMutation({
    onSuccess: async () => {
      void invalidateCadenceState();
      await Promise.all([utils.contact.contentViewed.invalidate(), utils.contact.responded.invalidate()]);
      void utils.cadenceTemplate.idV2.invalidate();
    },
  });
};

export const useCadenceChangeUserBulkMutation = () => {
  const utils = api.useUtils();
  const invalidateCadenceState = useInvalidateCadenceState();
  return api.cadenceBulk.changeUserBulk.useMutation({
    onSuccess: async () => {
      void invalidateCadenceState();
      await Promise.all([
        utils.contact.getConnected.invalidate(),
        utils.contact.getConnectedGroupedByOrg.invalidate(),
      ]);
    },
  });
};

export const useCadencePauseAllMutation = () => {
  const utils = api.useUtils();
  const invalidateCadenceState = useInvalidateCadenceState();
  return api.cadenceBulk.bulkPause.useMutation({
    onSuccess: async () => {
      void invalidateCadenceState();
      await utils.history.contact.invalidate();

      void utils.contact.contentViewed.invalidate();
      void utils.contact.responded.invalidate();
    },
  });
};

export const useCadenceReattachMutation = () => {
  const utils = api.useUtils();
  const invalidateCadenceState = useInvalidateCadenceState();
  return api.cadence.updateState.useMutation({
    onSuccess: async () => {
      void invalidateCadenceState();
      await utils.history.contact.invalidate();

      void utils.contact.contentViewed.invalidate();
      void utils.contact.responded.invalidate();
    },
  });
};

export const useCadenceResumeAllMutation = () => {
  const utils = api.useUtils();
  const invalidateCadenceState = useInvalidateCadenceState();
  return api.cadenceBulk.bulkResume.useMutation({
    onSuccess: async () => {
      void invalidateCadenceState();
      await utils.history.contact.invalidate();

      void utils.contact.contentViewed.invalidate();
      void utils.contact.responded.invalidate();
    },
  });
};

export const useBulkChangeCadenceMutation = () => {
  const utils = api.useUtils();
  const invalidateCadenceState = useInvalidateCadenceState();
  return api.cadenceBulk.bulkChangeCadence.useMutation({
    onSuccess: async () => {
      void invalidateCadenceState();
      await Promise.all([
        utils.cadenceTemplate.idV2.invalidate(),
        utils.cadenceTemplate.candidateCounts.invalidate(),
        utils.cadenceTemplate.candidateList.invalidate(),
        utils.contact.contentViewed.invalidate(),
        utils.contact.search.invalidate(),
      ]);
    },
  });
};

export const useCadenceDeleteStepMutation = () => {
  const invalidateCadenceState = useInvalidateCadenceState();
  return api.cadence.deleteStep.useMutation({
    onSuccess: () => {
      void invalidateCadenceState();
    },
  });
};

export const useCadenceCreateStepMutation = () => {
  const invalidateCadenceState = useInvalidateCadenceState();
  return api.cadence.createStep.useMutation({
    onSuccess: () => {
      void invalidateCadenceState();
    },
  });
};

/** Close a step out by hand (finished / skipped), optionally linking a history item to it. */
export const useCadenceResolveStepMutation = () => {
  const utils = api.useUtils();
  const invalidateCadenceState = useInvalidateCadenceState();
  return api.cadence.resolveStep.useMutation({
    onSuccess: () => {
      void invalidateCadenceState();
      // A linked history item now belongs to the step's campaign, and closing the last step
      // finishes the campaign, which writes its own history row.
      void utils.history.contact.invalidate();
    },
  });
};

export const useCadenceUpdateStepMutation = () => {
  const utils = api.useUtils();
  const queryClient = useQueryClient();
  // Resolved once at hook init — `getQueryKey(api.contact.byId)` returns
  // the operation prefix (no input filtering), so `setQueriesData`
  // updates EVERY cached `contact.byId` entry that contains the step.
  // The contact-detail page calls byId with a 4-field input
  // (contactId / cadenceId / userId / teamId); a partial-input setData
  // wouldn't match it, so we go through the underlying queryClient.
  const byIdQueryKey = getQueryKey(api.contact.byId);

  return api.cadence.updateStep.useMutation({
    // OPTIMISTIC UPDATE
    // Patch any cached contact.byId entry that contains this step so
    // the editor closes and the new content shows up instantly,
    // without waiting for the network round-trip + refetch.
    onMutate: async ({ stepId, data }) => {
      await queryClient.cancelQueries({ queryKey: byIdQueryKey });
      const snapshots = queryClient.getQueriesData({ queryKey: byIdQueryKey });

      queryClient.setQueriesData(
        { queryKey: byIdQueryKey },
        (old: unknown) => {
          if (old == null || typeof old !== 'object') return old;
          const oldRec = old as { activeCadence?: { cadenceSteps?: unknown[] | null } | null };
          const steps = oldRec.activeCadence?.cadenceSteps;
          if (!Array.isArray(steps)) return old;
          let touched = false;
          const updatedSteps = steps.map((s) => {
            const step = s as Record<string, unknown> & { id: string };
            if (step.id !== stepId) return s;
            touched = true;
            // Patch every server-accepted field that's in the payload.
            // body2 / subject / cc / bcc / notes can be empty strings,
            // and that's the user's intent — preserve as-is. Only skip
            // when the field is genuinely undefined.
            const patch: Record<string, unknown> = {};
            if (data.body2 !== undefined) patch.body2 = data.body2;
            if (data.subject !== undefined) patch.subject = data.subject;
            if (data.cc !== undefined) patch.cc = data.cc;
            if (data.bcc !== undefined) patch.bcc = data.bcc;
            if (data.notes !== undefined) patch.notes = data.notes;
            if (data.callStatus !== undefined) patch.callStatus = data.callStatus;
            if (data.postsToLike !== undefined) patch.postsToLike = data.postsToLike;
            if (data.onlyOpenLink !== undefined) patch.onlyOpenLink = data.onlyOpenLink;
            if (data.useEmailSignature !== undefined)
              patch.useEmailSignature = data.useEmailSignature;
            if (data.type !== undefined) patch.type = data.type;
            if (data.delay !== undefined) patch.delay = data.delay;
            if (data.siftConditions !== undefined) patch.siftConditions = data.siftConditions;
            return { ...step, ...patch };
          });
          if (!touched) return old;
          return {
            ...oldRec,
            activeCadence: { ...oldRec.activeCadence, cadenceSteps: updatedSteps },
          };
        },
      );

      return { snapshots };
    },
    // ROLLBACK on failure: restore every snapshot we took. The server
    // throws on validation failures (empty body for message, etc.) so
    // the rollback puts the old content back instantly.
    onError: (_err, _vars, ctx) => {
      const snapshots = (ctx as { snapshots?: ReturnType<typeof queryClient.getQueriesData> })
        ?.snapshots;
      if (!snapshots) return;
      for (const [key, data] of snapshots) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess: (data, { data: changes }) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
      void utils.globalReminder.paginated.invalidate();
      void utils.contact.contentViewed.invalidate();
      // An edit only changes this step's content, not any step's state, so it skips the full
      // campaign refresh. The type and delay are the exception: the enrolled list and touch graph
      // show them.
      if (changes.type !== undefined || changes.delay !== undefined) void utils.cadence.invalidate();
    },
  });
};

export const useChangeCadenceOwnerMutation = () => {
  const invalidateCadenceState = useInvalidateCadenceState();
  return api.cadence.changeUser.useMutation({
    onSuccess: () => {
      void invalidateCadenceState();
    },
  });
};